import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { usePrevious, useUpdateEffect } from "react-use";

import analytics from "fond/analytics";
import { apiSlice, useGetVersionQuery, useGetVersionStatusQuery } from "fond/api";
import { AsyncOperationState } from "fond/async/redux";
import { getCurrentProject } from "fond/project";
import { getWorkflow, StatusTypes } from "fond/project/redux";
import { LayerIds, Store, WorkflowStep } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { ErrorModal } from "fond/widgets";

import SolveErrors from "./errors/SolveErrors";
import { cancel, cancelDismissError, load, run, unload } from "./redux";
import SolveControl from "./SolveControl";
import SolveEditing from "./SolveEditing";
import SolveProgress from "./SolveProgress";
import SolveStatus from "./SolveStatus";

interface IProps {
  readOnly?: boolean;
}

const SolvePanel: React.FC<IProps> = ({ readOnly = false }: IProps) => {
  const dispatch = useAppDispatch();
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const versionId = useSelector((state: Store) => state.project.versionId);
  const { data: versionStatus } = useGetVersionStatusQuery(versionId, { skip: !versionId });
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });

  const { runStatus, cancelStatus, updateDataStatus } = useSelector((state: Store) => state.solve);
  const status = updateDataStatus === AsyncOperationState.failure ? null : versionStatus?.Status;
  const prevStatus = usePrevious(status);

  useEffect(() => {
    // When a design transitions to complete (from another known status)
    // Update the layers & subscription information
    if (prevStatus && prevStatus !== status && status === "Complete") {
      dispatch(
        apiSlice.util.invalidateTags([
          { type: "Layers", id: versionId },
          { type: "Subscription", id: "LIST" },
          { type: "FeatureTotals", id: versionId },
        ])
      );
    }
  }, [dispatch, status, versionId]);

  useEffect(() => {
    dispatch(load());
    return () => {
      dispatch(unload());
    };
  }, [dispatch, versionId]);

  useUpdateEffect(() => {
    const jobIds: { ID: string; JobID: string }[] = getWorkflow(versionStatus)?.map((w: WorkflowStep) => ({ ID: w.ID, JobID: w.JobID }));

    const projectStatusToEventName = {
      [StatusTypes.Complete]: "Solve completed",
      [StatusTypes.Cancelled]: "Solve cancelled",
      [StatusTypes.Terminated]: "Solve error",
    } as const;

    if (versionStatus?.Status && versionStatus?.Status in projectStatusToEventName) {
      analytics.track(projectStatusToEventName[versionStatus.Status], { projectId: project.ID, jobIds });
    }
  }, [versionStatus?.Status]);

  const onCancelClick = () => {
    analytics.track("Clicked cancel generate design");
    dispatch(cancel(versionId));
  };

  const hasEditedEquipment =
    ((versionStatus?.InputFiles?.cabinet || false) && (versionStatus.InputFiles?.closure || false)) ||
    ((versionStatus?.InputFiles?.t1_hub_geometry || false) && (versionStatus.InputFiles?.t2_hub_geometry || false)) ||
    versionStatus?.InputFiles?.[LayerIds.dropAssignment] ||
    false;

  const hasDropAssignments = Boolean(versionStatus?.InputFiles?.[LayerIds.dropAssignment]);

  const showProgress = status && [StatusTypes.Running, StatusTypes.Cancelling, StatusTypes.Terminated].includes(status);
  const showHubEditing =
    versionStatus?.HasSolution && status && [StatusTypes.Complete, StatusTypes.Terminated].includes(status) && !version?.Architecture?.IsFlexNap;

  return (
    <>
      <SolveStatus status={status} readOnly={readOnly} cancelStatus={cancelStatus} onCancel={onCancelClick} />
      <SolveErrors errors={versionStatus?.Error ?? []} />
      {showProgress && <SolveProgress workflow={getWorkflow(versionStatus)} />}
      {showHubEditing && <SolveEditing hasEditedEquipment={hasEditedEquipment} hasDropAssignments={hasDropAssignments} />}

      <SolveControl readOnly={readOnly} status={status} initialQuality={versionStatus?.WorkflowQuality || "express"} />

      {runStatus === AsyncOperationState.failure && <ErrorModal onClose={() => dispatch(run.dismissError())} />}
      {cancelStatus === AsyncOperationState.failure && <ErrorModal onClose={() => dispatch(cancelDismissError())} />}
    </>
  );
};

SolvePanel.displayName = "SolvePanel";
export default SolvePanel;
